import { useState, useEffect, useRef } from "react";
import calender from "../../../images/calender.svg";
import whitedown from "../../../images/whitedown.svg";
import whitecalender from "../../../images/whitecalender.svg";
import downarrow from "../../../images/downarrow.svg";

const DaySelector = ({
  isDarkMode,
  selectedDay,
  setSelectedDay,
  setSelectedHour,
  selectedHour,
  selectedDate,
  setSelectedDate,
}) => {
  // State for hours dropdown
  const [showHoursDropdown, setShowHoursDropdown] = useState(false);
  // State for calendar dropdown
  const [showCalendar, setShowCalendar] = useState(false);
  // State for dynamically generated days
  const [dayItems, setDayItems] = useState([]);
  // State for custom calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);

  // Refs for dropdowns (to handle clickaway)
  const hoursDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const hoursButtonRef = useRef(null);
  const calendarButtonRef = useRef(null);

  useEffect(() => {
    // Generate dynamic day items based on current date
    generateDayItems();

    // Generate calendar days for the current month
    generateCalendarDays(currentMonth);

    // Add click away listeners
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [currentMonth]);

  // Generate calendar days
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // Get the first day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayIndex = firstDay.getDay();

    // Get the last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Get the last day of the previous month
    const prevLastDay = new Date(year, month, 0);
    const prevDaysInMonth = prevLastDay.getDate();

    const days = [];

    // Add days from previous month
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      days.push({
        day: prevDaysInMonth - i,
        month: month - 1,
        year,
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
      });
    }

    // Add days from next month
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
      });
    }

    setCalendarDays(days);
  };

  // Handle click outside dropdowns
  const handleClickOutside = (event) => {
    if (
      hoursDropdownRef.current &&
      !hoursDropdownRef.current.contains(event.target) &&
      hoursButtonRef.current &&
      !hoursButtonRef.current.contains(event.target)
    ) {
      setShowHoursDropdown(false);
    }

    if (
      datePickerRef.current &&
      !datePickerRef.current.contains(event.target) &&
      calendarButtonRef.current &&
      !calendarButtonRef.current.contains(event.target)
    ) {
      setShowCalendar(false);
    }
  };

  // Calculate dropdown position for mobile
  const getDropdownPosition = (buttonRef, isHoursDropdown = false) => {
    if (!buttonRef.current) return {};

    const isMobile = window.innerWidth < 640;
    if (!isMobile) {
      // For desktop, position calendar consistently
      if (!isHoursDropdown) {
        return {
          position: "absolute",
          top: "100%",
          right: "0",
          width: "320px", // Wider calendar for desktop
        };
      }
      return {};
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const isTopHalf = rect.top < window.innerHeight / 2;

    // For hours dropdown which is smaller, we can position it directly under the button
    if (isHoursDropdown) {
      return {
        position: "fixed",
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        maxWidth: `${Math.min(280, window.innerWidth - 32)}px`,
      };
    }

    // For calendar dropdown which is larger
    return {
      position: "fixed",
      top: isTopHalf ? `${rect.bottom}px` : "auto",
      bottom: !isTopHalf ? `${window.innerHeight - rect.top}px` : "auto",
      left: "50%",
      transform: "translateX(-50%)",
      width: `${Math.min(300, window.innerWidth - 32)}px`,
    };
  };

  // Function to generate dynamic day items
  const generateDayItems = () => {
    const days = [];
    const today = new Date();
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

    // Add "Hrs" item
    days.push({
      text: "Hrs",
      icon: downarrow,
      darkIcon: whitedown,
      value: "hours",
      isDropdown: true,
    });

    // Add "TODAY" item
    days.push({ text: "TODAY", value: "today" });

    // Add next 4 days
    for (let i = 1; i <= 4; i++) {
      const nextDay = new Date(today);
      nextDay.setDate(today.getDate() + i);
      const dayName = dayNames[nextDay.getDay()];
      days.push({ text: dayName, value: dayName.toLowerCase() });
    }

    // Add calendar item
    days.push({
      text: "",
      icons: isDarkMode ? [whitecalender, whitedown] : [calender, downarrow],
      value: "calendar",
      isCalendar: true,
    });

    setDayItems(days);
  };

  // Generate hours for dropdown
  const generateHours = () => {
    const hours = [];
    for (let i = 1; i < 6; i++) {
      const hour = i > 1 ? `${i} hrs` : `${i} hr`;
      hours.push(hour);
    }
    return hours;
  };

  // Handle day selection
  const handleDaySelect = (day, e) => {
    if (day.isDropdown) {
      e.stopPropagation();
      setShowHoursDropdown((prev) => !prev);
      setShowCalendar(false);
      return;
    }

    if (day.isCalendar) {
      e.stopPropagation();
      setShowCalendar((prev) => !prev);
      setShowHoursDropdown(false);
      return;
    }

    setSelectedDay(day.text);
    setShowHoursDropdown(false);
    setShowCalendar(false);
  };

  // Handle hour selection
  const handleHourSelect = (hour, e) => {
    e.stopPropagation();
    setSelectedHour(hour);
    setShowHoursDropdown(false);
  };

  // Previous month
  const prevMonth = (e) => {
    e.stopPropagation();
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Next month
  const nextMonth = (e) => {
    e.stopPropagation();
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle date selection from custom calendar
  const handleDateSelect = (date, e) => {
    e.stopPropagation();
    const selectedDate = new Date(date.year, date.month, date.day);
    setSelectedDate(selectedDate);

    // Format date for display (e.g., "12 MAR")
    const monthNames = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    const day = selectedDate.getDate();
    const month = monthNames[selectedDate.getMonth()];
    const formattedDate = `${day} ${month}`;

    setSelectedDay(formattedDate);
    setShowCalendar(false);
  };

  // Format month for display
  const formatMonth = (date) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.day === today.getDate() &&
      date.month === today.getMonth() &&
      date.year === today.getFullYear()
    );
  };

  // Check if date is selected date
  const isSelectedDate = (date) => {
    if (!selectedDate) return false;
    return (
      date.day === selectedDate.getDate() &&
      date.month === selectedDate.getMonth() &&
      date.year === selectedDate.getFullYear()
    );
  };

  return (
    <ul className="scrollable max-sm:w-full flex gap-[30px] items-center">
      {dayItems.map((item, index) => {
        // Save refs for dropdown items
        const isHoursItem = item.isDropdown;
        const isCalendarItem = item.isCalendar;

        return (
          <li
            key={index}
            ref={
              isHoursItem
                ? hoursButtonRef
                : isCalendarItem
                ? calendarButtonRef
                : null
            }
            className={`max-sm:whitespace-nowrap max-sm:p-[15px_0px] py-3 s18:p-[27px_0px_27px] text-sm relative flex items-center cursor-pointer ${
              selectedDay === item.text
                ? "text-darkblue dark:text-yellow after:w-full after:bg-darkblue dark:after:bg-yellow"
                : "text-[#737373] dark:text-white after:w-[0px]"
            } s18:text-base font-medium !leading-tight s18:leading-[143%] hover:text-darkblue dark:hover:text-yellow transition-all duration-[0.3s] after:absolute after:content-[''] after:bg-darkblue after:left-[50%] after:translate-x-[-50%] after:h-[2px] after:mx-auto after:bottom-0 after:transition-all after:duration-[0.3s] hover:after:w-full dark:hover:after:bg-yellow`}
            onClick={(e) => handleDaySelect(item, e)}
          >
            {item.text && <span>{item.text}</span>}
            {item.icon && (
              <img
                src={isDarkMode && item.darkIcon ? item.darkIcon : item.icon}
                alt="icon"
                className={`min-w-[10px] ml-[4px] transition-transform duration-300 ${
                  showHoursDropdown ? "rotate-180" : ""
                }`}
              />
            )}
            {item.icons &&
              item.icons.map((icon, i) => (
                <img
                  key={i}
                  src={icon}
                  alt="icon"
                  className={
                    i > 0
                      ? `min-w-[10px] ml-[4px] transition-transform duration-300 ${
                          showCalendar ? "rotate-180" : ""
                        }`
                      : ""
                  }
                />
              ))}

            {/* Hours Dropdown */}
            {item.isDropdown && showHoursDropdown && (
              <div
                ref={hoursDropdownRef}
                className="absolute top-full left-0 z-20 mt-1 w-32 py-1 bg-white dark:bg-bluedarkmode shadow-lg rounded-md overflow-y-auto max-h-60"
                style={getDropdownPosition(hoursButtonRef, true)}
                onClick={(e) => e.stopPropagation()}
              >
                {generateHours().map((hour, hourIndex) => (
                  <div
                    key={hourIndex}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedHour === hour
                        ? "bg-gray-100 dark:bg-gray-700"
                        : ""
                    }`}
                    onClick={(e) => handleHourSelect(hour, e)}
                  >
                    {hour}
                  </div>
                ))}
              </div>
            )}

            {/* Custom DatePicker Component */}
            {item.isCalendar && showCalendar && (
              <div
                ref={datePickerRef}
                className="absolute top-full right-0 z-20 mt-1"
                style={getDropdownPosition(calendarButtonRef)}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={`custom-datepicker ${
                    isDarkMode ? "dark" : "light"
                  }`}
                >
                  <div className="datepicker-header">
                    <button onClick={(e) => prevMonth(e)}>&lt;</button>
                    <span>{formatMonth(currentMonth)}</span>
                    <button onClick={(e) => nextMonth(e)}>&gt;</button>
                  </div>
                  <div className="datepicker-days-header">
                    {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                      <div key={day} className="day-name">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="datepicker-days">
                    {calendarDays.map((day, index) => (
                      <div
                        key={index}
                        className={`day-cell ${
                          !day.isCurrentMonth ? "other-month" : ""
                        } ${isToday(day) ? "today" : ""} ${
                          isSelectedDate(day) ? "selected" : ""
                        }`}
                        onClick={(e) => handleDateSelect(day, e)}
                      >
                        {day.day}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </li>
        );
      })}

      {/* Custom DatePicker CSS */}
      <style jsx>{`
        .custom-datepicker {
          width: 100%;
          border-radius: 8px;
          overflow: hidden;
          font-family: inherit;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        .custom-datepicker.light {
          background-color: white;
          border: 1px solid #e5e7eb;
          color: #1f2937;
        }

        .custom-datepicker.dark {
          background-color: #1f2937;
          border: 1px solid #374151;
          color: white;
        }

        .datepicker-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          font-size: 16px;
        }

        .light .datepicker-header {
          background-color: #f3f4f6;
          border-bottom: 1px solid #e5e7eb;
        }

        .dark .datepicker-header {
          background-color: #2f425c;
          border-bottom: 1px solid #374151;
        }

        .datepicker-header button {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 4px 10px;
          border-radius: 4px;
        }

        .light .datepicker-header button {
          color: #1f2937;
        }

        .dark .datepicker-header button {
          color: white;
        }

        .light .datepicker-header button:hover {
          background-color: #e5e7eb;
        }

        .dark .datepicker-header button:hover {
          background-color: #4b5563;
        }

        .datepicker-days-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          text-align: center;
          padding: 8px 0;
          font-size: 14px;
        }

        .day-name {
          font-size: 14px;
          font-weight: 600;
        }

        .datepicker-days {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          grid-template-rows: repeat(6, auto);
          padding: 6px;
        }

        .day-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.2s, color 0.2s;
          font-size: 14px;
          height: 36px;
          width: 36px;
          margin: 2px auto;
          border-radius: 50%;
        }

        .light .day-cell:hover {
          background-color: #e5e7eb;
        }

        .dark .day-cell:hover {
          background-color: #4b5563;
        }

        .light .other-month {
          color: #9ca3af;
        }

        .dark .other-month {
          color: #6b7280;
        }

        .today {
          font-weight: bold;
          position: relative;
        }

        .today:after {
          content: "";
          position: absolute;
          bottom: 3px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          border-radius: 50%;
        }

        .light .today:after {
          background-color: #1e40af;
        }

        .dark .today:after {
          background-color: #f59e0b;
        }

        .light .selected {
          background-color: #1e40af;
          color: white;
        }

        .dark .selected {
          background-color: #f59e0b;
          color: black;
        }

        @media (max-width: 640px) {
          .scrollable {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .scrollable::-webkit-scrollbar {
            display: none;
          }

          .datepicker-days {
            padding: 2px;
          }

          .day-cell {
            font-size: 12px;
            height: 28px;
            width: 28px;
            margin: 1px auto;
          }

          .datepicker-days-header {
            padding: 4px 0;
          }

          .day-name {
            font-size: 12px;
          }

          .datepicker-header {
            padding: 8px 12px;
            font-size: 14px;
          }
        }
      `}</style>
    </ul>
  );
};

export default DaySelector;
